import { Box, CircularProgress, Typography } from '@mui/material';
import { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { PATHS } from 'routes/constants';
import { saveUrl2 } from 'redux/reducers/auth/reducer';
import { EasTableWithoutFilter } from '../../../../../ui/EasTableWithoutFilter';
import { ItemWithId, THandleAction, TTableStructure } from '../../../../../ui/EasTable/types';
import { TPageResponse } from '../../../../../redux/types';
import { PAGE_SIZE } from '../../../../../services/constants/env';
import {
	selectStoresByClientId,
	selectStoresByClientIdLoading,
} from '../../../../../redux/reducers/shopifyStores/selectors';
import { tableStructure, TShopifyStoresRow } from './tableStructure';
import { EditShopifyStoreDialog } from './EditShopifyStoreDialog';

export const ShopifyStoresList: FC = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { emId } = useParams<{ emId?: string }>();
	const storesByClientId = useSelector(selectStoresByClientId);
	const loading = useSelector(selectStoresByClientIdLoading);

	const [editStoreId, setEditStoreId] = useState<number | null>(null);
	const [isEditOpen, setIsEditOpen] = useState(false);

	const tableData: TPageResponse<TShopifyStoresRow & ItemWithId> | null = useMemo(() => {
		if (!storesByClientId) return null;
		const data = (storesByClientId.stores ?? []).map((store) => ({
			...store,
			id: store.store_id,
		}));
		return {
			data,
			pageNumber: 1,
			pageSize: PAGE_SIZE,
			totalCount: data.length,
		};
	}, [storesByClientId]);

	const shopifyStoresTableStructure = tableStructure as unknown as TTableStructure<
		keyof (TShopifyStoresRow & ItemWithId),
		TShopifyStoresRow & ItemWithId
	>[];

	const handleAction: THandleAction = useCallback((id, action) => {
		if (action === 'Edit') {
			setEditStoreId(id);
			setIsEditOpen(true);
		}
	}, []);

	const handleCloseEdit = useCallback(() => {
		setIsEditOpen(false);
		setEditStoreId(null);
	}, []);

	const handleLink = useCallback(
		(storeId: number) => {
			if (!emId) return;
			dispatch(saveUrl2(window.location.pathname + window.location.search));
			navigate(
				PATHS.EM_MANAGEMENT_SHOPIFY_STORE_DETAILS_PAGE.replace(':emId', emId).replace(
					':storeId',
					String(storeId),
				),
			);
		},
		[dispatch, emId, navigate],
	);

	return (
		<>
			<EasTableWithoutFilter<TShopifyStoresRow & ItemWithId>
				tableStructure={shopifyStoresTableStructure}
				items={tableData}
				itemsIsLoading={loading}
				handleAction={handleAction}
				handleLink={handleLink}
				skeletonRows={[1]}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Shopify Stores
					</Typography>
					{loading && <CircularProgress size={20} />}
				</Box>
			</EasTableWithoutFilter>
			<EditShopifyStoreDialog open={isEditOpen} storeId={editStoreId} onClose={handleCloseEdit} />
		</>
	);
};
